import Box from '@liquidweb-dev/undercurrent/Box';
import { listItemButtonClasses } from '@liquidweb-dev/undercurrent/ListItemButton';
import type { Theme } from '@liquidweb-dev/undercurrent/styles';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import type { useLatestNotifications } from 'modules/queries/notification/useCurrent';
import type { ServerListEntry } from './groupServersByParent';
import ServerDetails from './ServerDetails';
import ServersListRow from './ServersListRow';

export interface ServersListNestedGroupProps {
	entry: ServerListEntry<AssetDetails>;
	notifications?: ReturnType<typeof useLatestNotifications>['data'];
}

const connectorBase = {
	content: '""',
	position: 'absolute',
	// above the row's hover background, without catching clicks
	zIndex: 1,
	pointerEvents: 'none',
} as const;

/**
 * The tree line runs down the centre of the parent's status badge, starting at
 * the first child. Every child but the last has a square branch into its badge
 * with the line carrying on; the last child's line ends in a rounded corner.
 */
const childConnectorSx = ({ size, vars }: Theme) => {
	const line = `1px solid ${vars.palette.uc.border.main}`;
	// centres the 1px line on the badge, and reaches the child's badge
	const left = `calc(${size.md} - 0.5px)`;
	const width = `calc(${size.xS} + 0.5px)`;

	return {
		'&::before': {
			...connectorBase,
			top: 0,
			bottom: 0,
			left,
			borderLeft: line,
		},
		'&:last-child::before': {
			bottom: 'auto',
			height: '50%',
			width,
			borderBottom: line,
			borderBottomLeftRadius: size.xXS,
		},
		'&:not(:last-child)::after': {
			...connectorBase,
			top: 'calc(50% - 1px)',
			left,
			width,
			borderTop: line,
		},
	};
};

/**
 * A private cloud parent row followed by its loaded children, joined by a tree
 * line. The group is one list item: rows inside it have no separators, and the
 * group as a whole has the bottom border.
 */
const ServersListNestedGroup = ({
	entry: { server, isParent, children, numberOfChildServers },
	notifications,
}: ServersListNestedGroupProps) => (
	<Box
		component="li"
		sx={({ vars }) => ({
			listStyle: 'none',
			borderBottom: `1px solid ${vars.palette.uc.border.weak}`,
			'&:last-child': { borderBottom: 'none' },
		})}
	>
		<Box
			component="ul"
			sx={{
				margin: 0,
				padding: 0,
				listStyle: 'none',
				// doubled class to outrank the theme's per-row border
				[`&& .${listItemButtonClasses.root}`]: { borderBottom: 'none' },
			}}
		>
			<ServersListRow
				assetDetails={server}
				notification={notifications?.[server.uniq_id]}
				numberOfChildServers={isParent ? numberOfChildServers : undefined}
			>
				<ServerDetails
					assetDetails={server}
					variant="row"
					numberOfChildServers={isParent ? numberOfChildServers : undefined}
				/>
			</ServersListRow>

			{children.map((child) => (
				<ServersListRow
					key={child.uniq_id}
					assetDetails={child}
					notification={notifications?.[child.uniq_id]}
					isNested
					sx={childConnectorSx}
				>
					<ServerDetails assetDetails={child} variant="row" />
				</ServersListRow>
			))}
		</Box>
	</Box>
);

export default ServersListNestedGroup;
